/**
 * Developer Admin Layout - 개발자용 관리 대시보드 레이아웃
 * 
 * Super Admin과 별도로 데이터 통계, 정합성 체크, 정리 도구 제공
 * AdminFire 인스턴스 사용으로 일반 유저 세션과 분리
 */

import { Trans } from '@lingui/macro';
import {
    BarChart3, Database, Trash2, HardDrive, LogOut,
    Building2, MessageSquare, ChevronRight, Loader2, CalendarClock
} from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { cn } from '../../lib/utils';
import { SuperAdmin } from '../../services/SuperAdmin';
import { AdminFire } from '../../services/SuperAdminFirebase';

interface NavItem {
    path: string;
    label: string;
    icon: React.ElementType;
}

const NAV_ITEMS: NavItem[] = [
    { path: '/dev-admin', label: 'Dashboard', icon: BarChart3 },
    { path: '/dev-admin/collections', label: 'Collections', icon: Database },
    { path: '/dev-admin/workspaces', label: 'Workspaces', icon: Building2 },
    { path: '/dev-admin/rooms', label: 'Rooms', icon: MessageSquare },
    { path: '/dev-admin/cleanup', label: 'Cleanup', icon: Trash2 },
    { path: '/dev-admin/storage', label: 'Storage', icon: HardDrive },
    { path: '/dev-admin/scheduler', label: 'Scheduler', icon: CalendarClock },
];

const DevAdminLayout: React.FC = () => {
    const location = useLocation();
    const [authChecked, setAuthChecked] = useState(false);
    const [isAuthenticated, setIsAuthenticated] = useState(false);

    useEffect(() => {
        // AdminFire 인스턴스의 Auth 상태 확인
        const checkAuth = async () => {
            await AdminFire.waitForReady();
            setIsAuthenticated(AdminFire.isAuthenticated || SuperAdmin.isLoggedIn);
            setAuthChecked(true);
        };
        checkAuth();
    }, []);

    // 인증 체크 전 로딩 표시
    if (!authChecked) {
        return (
            <div className="flex h-screen items-center justify-center bg-slate-50">
                <div className="text-center">
                    <Loader2 className="w-8 h-8 animate-spin text-emerald-600 mx-auto mb-2" />
                    <p className="text-slate-500">인증 확인 중...</p>
                </div>
            </div>
        );
    }

    // 인증되지 않은 경우
    if (!isAuthenticated) {
        return (
            <div className="flex h-screen items-center justify-center bg-slate-50">
                <div className="text-center p-8 bg-white rounded-xl shadow-lg max-w-md">
                    <h2 className="text-xl font-bold text-slate-900 mb-2">로그인이 필요합니다</h2>
                    <p className="text-slate-500 mb-6">
                        Developer Dashboard에 접근하려면 관리자 계정으로 로그인하세요.
                    </p>
                    <button
                        onClick={() => window.location.href = '/super-login'}
                        className="px-6 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors"
                    >
                        로그인 페이지로 이동
                    </button>
                </div>
            </div>
        );
    }

    const handleLogout = async () => {
        try {
            SuperAdmin.clear();
            await AdminFire.signOut();
            window.location.href = '/super-login';
        } catch (error) {
            console.error('Logout error:', error);
            window.location.href = '/super-login';
        }
    };

    const isActiveRoute = (path: string) => {
        if (path === '/dev-admin') {
            return location.pathname === '/dev-admin';
        }
        return location.pathname.startsWith(path);
    };

    return (
        <div className="flex h-screen bg-slate-50">
            {/* Sidebar */}
            <aside className="w-64 bg-slate-900 text-white flex flex-col">
                {/* Logo */}
                <div className="p-4 border-b border-slate-700">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-emerald-600/20 flex items-center justify-center">
                            <Database className="w-5 h-5 text-emerald-400" />
                        </div>
                        <div>
                            <h1 className="font-bold text-lg">Dev Admin</h1>
                            <p className="text-xs text-slate-400">Data Management</p>
                        </div>
                    </div>
                </div>

                {/* Navigation */}
                <nav className="flex-1 p-3 space-y-1">
                    {NAV_ITEMS.map((item) => {
                        const Icon = item.icon;
                        const isActive = isActiveRoute(item.path);
                        return (
                            <NavLink
                                key={item.path}
                                to={item.path}
                                className={cn(
                                    'flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all',
                                    isActive
                                        ? 'bg-emerald-600 text-white'
                                        : 'text-slate-400 hover:text-white hover:bg-slate-800'
                                )}
                            >
                                <Icon className="w-5 h-5" />
                                <span className="font-medium">{item.label}</span>
                                {isActive && <ChevronRight className="w-4 h-4 ml-auto" />}
                            </NavLink>
                        );
                    })}
                </nav>

                {/* Admin Info & Logout */}
                <div className="p-4 border-t border-slate-700 space-y-3">
                    {SuperAdmin.info && (
                        <div className="text-sm">
                            <p className="text-slate-400">Logged in as</p>
                            <p className="text-white font-medium truncate">
                                {SuperAdmin.info.name}
                            </p>
                        </div>
                    )}
                    <button
                        onClick={handleLogout}
                        className="flex items-center gap-2 w-full px-3 py-2 text-sm text-slate-400 hover:text-red-400 hover:bg-slate-800 rounded-lg transition-colors"
                    >
                        <LogOut className="w-4 h-4" />
                        <span><Trans>Logout</Trans></span>
                    </button>
                </div>
            </aside>

            {/* Main Content */}
            <main className="flex-1 overflow-hidden">
                <Outlet />
            </main>
        </div>
    );
};

export default DevAdminLayout;
